import {
  fetchDashboardExperience,
  fetchDashboardExperienceV2,
  type DashboardExperienceResponse,
} from '@library/api-dashboard-experience'
import { datadogLogs } from '@datadog/browser-logs'
import { datadogRum } from '@datadog/browser-rum'
import { useEffect, useRef } from 'react'
import useSWR from 'swr'

import { type DashboardExperienceCacheKey } from '../configs'
import { useDashboardExperienceApiConfig } from './useDashboardExperienceApiConfig'
import {
  DASHBOARD_EXPERIENCE_TELEMETRY_EVENTS,
  type DashboardExperienceTelemetryContext,
  getDashboardExperienceTelemetryTrackingKey,
} from './dashboardExperienceTelemetry'

type UseDashboardExperienceResult = {
  page_title: DashboardExperienceResponse['page_title']
  order_tabs: DashboardExperienceResponse['order_tabs']
  orders: NonNullable<DashboardExperienceResponse['orders']>
  actions: NonNullable<DashboardExperienceResponse['actions']>
  downloadApp?: DashboardExperienceResponse['downloadApp']
  rafCampaign?: DashboardExperienceResponse['rafCampaign']
  rafStatus?: DashboardExperienceResponse['rafStatus']
  sections?: DashboardExperienceResponse['sections']
  error: Error | undefined
  isLoading: boolean
  isSessionExpired?: boolean
  telemetry?: DashboardExperienceTelemetryContext
}

const initialState: UseDashboardExperienceResult = {
  page_title: '',
  order_tabs: [],
  orders: [],
  actions: [],
  error: undefined,
  isLoading: true,
}

const getStatusCode = (error: unknown): number | undefined => {
  if (typeof error === 'object' && error !== null && 'status' in error) {
    const status = (error as { status?: unknown }).status

    if (typeof status === 'number') {
      return status
    }
  }

  if (typeof error === 'object' && error !== null && 'message' in error) {
    const message = (error as { message?: unknown }).message

    if (typeof message === 'string') {
      const statusMatch = message.match(/(?:status=|status(?: code)?:\s*|with\s)(\d{3})/i)

      if (statusMatch) {
        return Number(statusMatch[1])
      }
    }
  }

  return undefined
}

const fetchDashboardExperienceForVersion = ([_apiKey, apiVersion]: DashboardExperienceCacheKey) =>
  apiVersion === 'v2' ? fetchDashboardExperienceV2() : fetchDashboardExperience()

const useDashboardExperience = (): UseDashboardExperienceResult => {
  const { cacheKey, isReady, telemetryContext } = useDashboardExperienceApiConfig()
  const { data, error } = useSWR(isReady ? cacheKey : null, fetchDashboardExperienceForVersion)
  const variantViewTrackingKey = getDashboardExperienceTelemetryTrackingKey(telemetryContext)
  const trackedAllocationKey = useRef<string | null>(null)
  const trackedVariantViewKey = useRef<string | null>(null)
  const trackedApiSuccessKey = useRef<string | null>(null)

  useEffect(() => {
    if (
      !telemetryContext.decisionReady ||
      trackedAllocationKey.current === variantViewTrackingKey
    ) {
      return
    }

    trackedAllocationKey.current = variantViewTrackingKey
    datadogRum.addAction(DASHBOARD_EXPERIENCE_TELEMETRY_EVENTS.experimentAllocated, {
      ...telemetryContext,
      allocated: true,
    })
  }, [telemetryContext, variantViewTrackingKey])

  useEffect(() => {
    if (!isReady || trackedVariantViewKey.current === variantViewTrackingKey) {
      return
    }

    trackedVariantViewKey.current = variantViewTrackingKey
    datadogRum.addAction(DASHBOARD_EXPERIENCE_TELEMETRY_EVENTS.variantViewed, telemetryContext)
  }, [isReady, telemetryContext, variantViewTrackingKey])

  useEffect(() => {
    if (!data || error) {
      return
    }

    const trackingKey = `${variantViewTrackingKey}:api-success`

    if (trackedApiSuccessKey.current === trackingKey) {
      return
    }

    trackedApiSuccessKey.current = trackingKey
    datadogRum.addAction(DASHBOARD_EXPERIENCE_TELEMETRY_EVENTS.apiSuccess, {
      ...telemetryContext,
      statusCode: 200,
    })
  }, [data, error, telemetryContext, variantViewTrackingKey])

  if (error) {
    const errorMessage = `Error fetching dashboard experience in myHome ${JSON.stringify(error)}`
    const statusCode = getStatusCode(error)

    datadogLogs.logger.error(errorMessage)
    datadogRum.addError(error, {
      page: 'MyHomePage',
      errorMessage: errorMessage,
    })
    if (statusCode && statusCode !== 200) {
      datadogRum.addAction('dashboard_experience_api_error', {
        page: 'MyHomePage',
        statusCode,
      })
    }

    return {
      ...initialState,
      error,
      isLoading: false,
      ...(statusCode === 401 ? { isSessionExpired: true } : {}),
    }
  }

  if (!data) {
    return initialState
  }

  return {
    page_title: data.page_title,
    order_tabs: data.order_tabs ?? initialState.order_tabs,
    orders: data.orders ?? initialState.orders,
    actions: data.actions ?? initialState.actions,
    ...(data.downloadApp !== undefined ? { downloadApp: data.downloadApp } : {}),
    ...(data.rafCampaign !== undefined ? { rafCampaign: data.rafCampaign } : {}),
    ...(data.rafStatus !== undefined ? { rafStatus: data.rafStatus } : {}),
    ...(data.sections !== undefined ? { sections: data.sections } : {}),
    error,
    isLoading: false,
    telemetry: telemetryContext,
  }
}

export { useDashboardExperience }
